
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Video, AiAnalysis, AiAnalysisKpis, Kpi, UserRole, Company } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import { AuthContext } from '../../contexts/AuthContext';
import Spinner from '../../components/common/Spinner';
import StatusBadge from '../../components/common/StatusBadge';
import { Timestamp } from 'firebase/firestore';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import KpiScoreChart from '../../components/common/KpiScoreChart';
import { generateAnalysisPdf } from '../../services/pdfService';
import AnalysisCharts, { AnalysisChartsRef } from '../../components/common/AnalysisCharts';


const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const SaveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>;
const CancelIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>;
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>;


const KpiEditor: React.FC<{
    kpiKey: string;
    kpiData: Kpi | string;
    isEditing: boolean;
    onChange: (key: string, value: Kpi | string) => void;
}> = ({ kpiKey, kpiData, isEditing, onChange }) => {
    // Determine if this is a scored KPI object or just a string
    const isScoredKpi = typeof kpiData === 'object' && kpiData !== null && 'score' in kpiData;
    const title = kpiKey.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    const handleAnalysisChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (isScoredKpi) {
            onChange(kpiKey, { ...(kpiData as Kpi), analysis: e.target.value });
        } else {
            onChange(kpiKey, e.target.value);
        }
    };

    const handleScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isScoredKpi) {
            let score = parseInt(e.target.value, 10);
            if (isNaN(score)) score = 0;
            if (score < 1) score = 1;
            if (score > 10) score = 10;
            onChange(kpiKey, { ...(kpiData as Kpi), score });
        }
    };

    return (
        <div className="bg-gray-800/50 p-4 rounded-lg">
            <h3 className="font-semibold text-lg text-blue-300 mb-2">{title}</h3>
            {isEditing ? (
                <div className="space-y-3">
                    {isScoredKpi && (
                        <div>
                            <label className="text-sm text-gray-400">Score (1-10)</label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={(kpiData as Kpi).score}
                                onChange={handleScoreChange}
                                className="w-20 px-2 py-1 bg-gray-900 border border-gray-700 rounded-md ml-2 text-white"
                            />
                        </div>
                    )}
                    <div>
                        <label className="text-sm text-gray-400 block mb-1">Analysis</label>
                        <textarea
                            value={isScoredKpi ? (kpiData as Kpi).analysis : (kpiData as string)}
                            onChange={handleAnalysisChange}
                            rows={3}
                            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                        />
                    </div>
                </div>
            ) : (
                <div className="flex justify-between items-center gap-4">
                    <p className="text-gray-300 flex-1">{isScoredKpi ? (kpiData as Kpi).analysis : (kpiData as string)}</p>
                    {isScoredKpi && (
                       <KpiScoreChart score={(kpiData as Kpi).score} />
                    )}
                </div>
            )}
        </div>
    );
};

const VideoDetailPage: React.FC = () => {
    const { videoId } = useParams<{ videoId: string }>();
    const [video, setVideo] = useState<Video | null>(null);
    const [company, setCompany] = useState<Company | null>(null);
    const [analysis, setAnalysis] = useState<AiAnalysis | null>(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editedKpis, setEditedKpis] = useState<AiAnalysisKpis | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [videoToDelete, setVideoToDelete] = useState<Video | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const chartsRef = useRef<AnalysisChartsRef>(null);

    const authContext = useContext(AuthContext);
    const navigate = useNavigate();
    const user = authContext?.user;

    const getEmbeddableUrl = (url: string) => {
        if (url.includes('drive.google.com')) {
            const regex = /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/;
            const match = url.match(regex);
            if (match && match[1]) {
                return `https://drive.google.com/file/d/${match[1]}/preview`;
            }
        }
        return url;
    };

    const fetchData = useCallback(async () => {
        if (!videoId) return;
        setLoading(true);
        try {
            const fetchedVideo = await firestoreService.getVideo(videoId);
            setVideo(fetchedVideo);
            if (fetchedVideo) {
                // Fetch company for PDF logos
                const fetchedCompany = await firestoreService.getCompany(fetchedVideo.companyId);
                setCompany(fetchedCompany);

                if (fetchedVideo.aiAnalysisId) {
                    const fetchedAnalysis = await firestoreService.getAnalysis(fetchedVideo.aiAnalysisId);
                    setAnalysis(fetchedAnalysis);
                    setEditedKpis(fetchedAnalysis?.kpis || null);
                }
            }
        } catch (error) {
            console.error("Failed to fetch data:", error);
        } finally {
            setLoading(false);
        }
    }, [videoId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleKpiChange = (key: string, value: Kpi | string) => {
        setEditedKpis(prev => prev ? { ...prev, [key]: value } : null);
    };

    const handleSave = async () => {
        if (!analysis || !editedKpis || !user || !user.tenantId) return;
        setIsSaving(true);
        try {
            const newVersion = {
                v: (analysis.versions?.length || 0) + 1,
                editor: user.uid,
                timestamp: Timestamp.now(),
                kpis: editedKpis,
            };

            const updatedAnalysisData = {
                ...analysis,
                kpis: editedKpis,
                versions: [...(analysis.versions || []), newVersion],
            };
            
            // Fixed: Added tenantId to the Omit type object
            const dataToSave: Omit<AiAnalysis, 'analysisId' | 'generatedAt'> = {
                videoId: updatedAnalysisData.videoId,
                tenantId: user.tenantId,
                companyId: updatedAnalysisData.companyId,
                kpis: updatedAnalysisData.kpis,
                versions: updatedAnalysisData.versions,
            };

            // Fixed: Passed tenantId as first argument to saveAnalysis
            await firestoreService.saveAnalysis(user.tenantId, dataToSave, analysis.analysisId);
            setIsEditing(false);
            await fetchData(); // Refresh data to show latest version
        } catch (error) {
            console.error("Failed to save changes:", error);
            alert("Could not save the analysis.");
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleCancel = () => {
        setEditedKpis(analysis?.kpis || null);
        setIsEditing(false);
    };

    const handleDeleteClick = (video: Video) => {
        setVideoToDelete(video);
    };

    const confirmDeleteVideo = async () => {
        if (!videoToDelete) return;
        setIsDeleting(true);
        try {
            await firestoreService.deleteVideo(videoToDelete.videoId);
            navigate(`/videos/company/${videoToDelete.companyId}`);
        } catch (error) {
            console.error("Failed to delete video:", error);
            alert("There was an error deleting the video.");
        } finally {
            setIsDeleting(false);
            setVideoToDelete(null);
        }
    };
    
    const handleDownloadPdf = async () => {
        if (video && analysis) {
            const chartImages = chartsRef.current?.getChartImagesAsBase64();
            // Pass company data (with logos) to the PDF service
            // Updated to await the async function
            await generateAnalysisPdf(video, analysis, chartImages, company || undefined);
        }
    };

    if (loading) return <div className="flex justify-center items-center h-full"><Spinner size="lg" /></div>;
    if (!video) return <div className="text-center text-red-400">Video not found.</div>;

    // Fixed: Replaced SUPER_ADMIN with PLATFORM_OWNER and COMPANY_ADMIN with TENANT_ADMIN
    const canEdit = user?.role === UserRole.PLATFORM_OWNER || user?.role === UserRole.TENANT_ADMIN || user?.role === UserRole.ANALYST;

    return (
        <div>
            <Link to={`/videos/company/${video.companyId}`} className="text-blue-400 hover:text-blue-300 mb-6 inline-flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                Back to Company Videos
            </Link>

            <div className="bg-gray-900 rounded-lg p-6 md:p-8">
                <div className="flex justify-between items-start mb-4">
                    <h1 className="text-4xl font-bold text-white">{video.title}</h1>
                    <StatusBadge status={video.status} />
                </div>
                <p className="text-gray-400 mb-6 max-w-3xl">{video.description}</p>
                
                <div className="bg-black rounded-lg overflow-hidden aspect-video mb-8 shadow-2xl shadow-blue-900/20">
                     {video.storagePath ? (
                        <iframe 
                            src={getEmbeddableUrl(video.storagePath)}
                            width="100%" 
                            height="100%" 
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                            title={video.title}
                            className="border-0"
                        ></iframe>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500">
                             <img src={video.thumbnailUrl} alt="Video thumbnail" className="max-h-full max-w-full object-contain" />
                             <p className="mt-2 text-sm italic">La previsualización del video no está disponible (no se proporcionó URL).</p>
                        </div>
                    )}
                </div>

                <div className="flex justify-between items-center mb-4 border-b-2 border-blue-500 pb-2">
                    <h2 className="text-3xl font-bold text-white">AI Analysis</h2>
                     <div className="flex items-center gap-2">
                        {!isEditing && (
                            <button
                                onClick={handleDownloadPdf}
                                disabled={!analysis}
                                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-500 disabled:cursor-not-allowed flex items-center gap-2"
                                title={analysis ? "Download PDF Report" : "Analysis not available"}
                            >
                                <DownloadIcon /> Download
                            </button>
                        )}
                        {canEdit && (
                            <div className="flex gap-2">
                                {isEditing ? (
                                    <>
                                        <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-500 flex items-center gap-2">
                                            {isSaving ? <Spinner size="sm" /> : <SaveIcon />} Save
                                        </button>
                                        <button onClick={handleCancel} disabled={isSaving} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 flex items-center gap-2">
                                            <CancelIcon /> Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => setIsEditing(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2">
                                            <EditIcon /> Edit
                                        </button>
                                        <button onClick={() => handleDeleteClick(video)} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2">
                                            <DeleteIcon /> Delete Video
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {analysis && editedKpis ? (
                    <div>
                        <AnalysisCharts ref={chartsRef} kpis={editedKpis} />
                        <div className="space-y-4">
                            {/* Dynamically render all KPI keys */}
                            {Object.keys(editedKpis).map(key => (
                                <KpiEditor
                                    key={key}
                                    kpiKey={key}
                                    kpiData={editedKpis[key]}
                                    isEditing={isEditing}
                                    onChange={handleKpiChange}
                                />
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-10 bg-gray-800/50 rounded-lg">
                        <p className="text-gray-400">Analysis is not yet available for this video.</p>
                    </div>
                )}
            </div>
            <ConfirmationModal
                isOpen={!!videoToDelete}
                onClose={() => setVideoToDelete(null)}
                onConfirm={confirmDeleteVideo}
                title="Delete Video"
                message={<>Are you sure you want to delete the video <strong>{videoToDelete?.title}</strong>? This will also delete its AI analysis. This action is irreversible.</>}
                isConfirming={isDeleting}
            />
        </div>
    );
};

export default VideoDetailPage;
